import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import { useT, LANGS } from "@/lib/i18n";
import { NAV } from "@/constants/testIds";
import { Flame, LogOut, Shield, ChevronDown, Check, Globe, Gauge } from "lucide-react";

export default function Navbar() {
    const { lang, setLang, user, logout, quota } = useApp();
    const t = useT(lang);
    const loc = useLocation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const menuRef = useRef(null);
    const active = LANGS.find((l) => l.code === lang) || LANGS[0];

    useEffect(() => {
        const onDoc = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const scrollOrGoto = (id) => {
        if (loc.pathname !== "/") { navigate("/#" + id); return; }
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <header className="sticky top-0 z-40 border-b border-[#253046] bg-[#080B10]/85 backdrop-blur-xl">
            <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-8 h-16">
                <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2 group">
                    <span className="flame-gradient w-8 h-8 rounded-md grid place-items-center flame-glow">
                        <Flame className="w-4 h-4 text-black" strokeWidth={3} />
                    </span>
                    <span className="font-display text-xl font-extrabold uppercase tracking-tight">
                        Shorts <span className="flame-text">Factory</span>
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-300">
                    <Link to="/" data-testid={NAV.home} className="hover:text-white kinetic">{t("nav_home")}</Link>
                    <button onClick={() => scrollOrGoto("modules")} className="hover:text-white kinetic">{t("cta_learn")}</button>
                    <button onClick={() => scrollOrGoto("pricing")} data-testid={NAV.pricing} className="hover:text-white kinetic">{t("nav_pricing")}</button>
                    {user && <Link to="/studio" data-testid={NAV.studio} className="hover:text-white kinetic">{t("nav_studio")}</Link>}
                </nav>

                <div className="flex items-center gap-2">
                    <div ref={menuRef} className="relative">
                        <button
                            onClick={() => setOpen((v) => !v)}
                            data-testid={NAV.langToggle}
                            aria-haspopup="listbox"
                            aria-expanded={open}
                            className="font-mono-sf text-[11px] tracking-widest px-2.5 py-1.5 rounded border border-[#253046] hover:border-[#FF4D00] hover:text-white text-slate-300 kinetic flex items-center gap-1.5"
                        >
                            <Globe className="w-3.5 h-3.5" />
                            <span>{active.flag}</span>
                            <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
                        </button>
                        {open && (
                            <div data-testid="lang-menu" role="listbox" className="absolute right-0 mt-2 w-40 rounded-md border border-[#253046] bg-[#0F141C] shadow-2xl overflow-hidden z-50">
                                {LANGS.map((l) => (
                                    <button
                                        key={l.code}
                                        role="option"
                                        aria-selected={l.code === lang}
                                        data-testid={`lang-option-${l.code}`}
                                        onClick={() => { setLang(l.code); setOpen(false); }}
                                        className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left text-sm kinetic ${l.code === lang ? "bg-[#FF4D00]/10 text-white" : "text-slate-300 hover:bg-[#161D2A] hover:text-white"}`}
                                    >
                                        <span className="flex items-center gap-2">
                                            <span className="font-mono-sf text-[10px] tracking-widest text-slate-500">{l.flag}</span>
                                            <span>{l.label}</span>
                                        </span>
                                        {l.code === lang && <Check className="w-4 h-4 text-[#FF4D00]" />}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {user ? (
                        <>
                            {quota && quota.limit > 0 && (() => {
                                const pct = Math.min(100, Math.round((quota.used / quota.limit) * 100));
                                const tone = pct >= 100 ? "border-red-500/50 text-red-400" : pct >= 80 ? "border-[#FFB800]/50 text-[#FFB800]" : "border-[#FF4D00]/40 text-[#FF4D00]";
                                return (
                                    <Link
                                        to="/studio"
                                        data-testid="nav-quota-badge"
                                        title={`${quota.used}/${quota.limit} Shorts`}
                                        className={`hidden sm:flex items-center gap-1.5 text-[11px] font-mono-sf px-2.5 py-1.5 rounded border ${tone} bg-[#0F141C] hover:bg-[#161D2A] kinetic tracking-widest`}
                                    >
                                        <Gauge className="w-3.5 h-3.5" />
                                        <span data-testid="nav-quota-text">{quota.used}/{quota.limit}</span>
                                    </Link>
                                );
                            })()}
                            <span data-testid={NAV.userBadge} className="hidden sm:block text-xs text-slate-400 font-mono-sf px-2">{user.email}</span>
                            <Link
                                to="/admin/leads"
                                data-testid="nav-admin-link"
                                className="hidden md:flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border border-[#253046] hover:border-[#FF4D00] text-slate-300 hover:text-white kinetic"
                                title="Admin"
                            >
                                <Shield className="w-3.5 h-3.5" />
                            </Link>
                            <button onClick={() => { logout(); navigate("/"); }} data-testid={NAV.logout} className="text-xs px-3 py-1.5 rounded border border-[#253046] hover:border-[#FF4D00] text-slate-300 hover:text-white kinetic flex items-center gap-1.5">
                                <LogOut className="w-3.5 h-3.5" /> {t("nav_logout")}
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/auth?mode=login" data-testid={NAV.login} className="text-xs px-3 py-1.5 rounded text-slate-300 hover:text-white kinetic">{t("nav_login")}</Link>
                            <Link to="/auth?mode=register" data-testid={NAV.signup} className="text-xs px-3.5 py-1.5 rounded flame-gradient font-semibold text-black hover:brightness-110 kinetic">
                                {t("nav_signup")}
                            </Link>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
}
